import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { produtosAPI } from '../utils/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Save, Package } from 'lucide-react';
import Swal from 'sweetalert2';

export const ProdutoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  usePageTitle(isEdit ? 'Editar Produto' : 'Novo Produto');

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [formData, setFormData] = useState({
    nome: '',
    quantidade: 0,
    observacao_padrao: ''
  });

  useEffect(() => {
    if (isEdit) {
      fetchProduto();
    }
  }, [id]);

  const fetchProduto = async () => {
    try {
      const response = await produtosAPI.getOne(id);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching produto:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Produto não encontrado.',
        confirmButtonColor: '#0D9488'
      });
      navigate('/produtos');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Campo obrigatório',
        text: 'O nome do produto é obrigatório.',
        confirmButtonColor: '#0D9488'
      });
      return;
    }

    setLoading(true);
    try {
      if (isEdit) {
        await produtosAPI.update(id, formData);
        Swal.fire({
          icon: 'success',
          title: 'Atualizado!',
          text: 'Produto atualizado com sucesso.',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        await produtosAPI.create(formData);
        Swal.fire({
          icon: 'success',
          title: 'Criado!',
          text: 'Produto criado com sucesso.',
          timer: 1500,
          showConfirmButton: false
        });
      }
      navigate('/produtos');
    } catch (error) {
      console.error('Error saving produto:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível guardar o produto.',
        confirmButtonColor: '#0D9488'
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="produto-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/produtos">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            {isEdit ? 'Editar Produto' : 'Novo Produto'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isEdit ? 'Atualize os dados do produto' : 'Preencha os dados do novo produto'}
          </p>
        </div>
      </div>

      {/* Form */}
      <Card className="border-slate-200 max-w-xl">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Package className="h-5 w-5 text-amber-600" />
            Dados do Produto
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="nome" className="text-slate-700">
                Nome do Produto <span className="text-red-500">*</span>
              </Label>
              <Input
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                placeholder="Ex: Paracetamol 500mg"
                data-testid="produto-nome-input"
              />
            </div>

            {/* Quantidade */}
            <div className="space-y-2">
              <Label htmlFor="quantidade" className="text-slate-700">
                Quantidade em Stock
              </Label>
              <Input
                id="quantidade"
                name="quantidade"
                type="number"
                min="0"
                value={formData.quantidade}
                onChange={handleChange}
                placeholder="0"
                data-testid="produto-quantidade-input"
              />
            </div>

            {/* Observação Padrão */}
            <div className="space-y-2">
              <Label htmlFor="observacao_padrao" className="text-slate-700">
                Observação Padrão
              </Label>
              <Textarea
                id="observacao_padrao"
                name="observacao_padrao"
                value={formData.observacao_padrao || ''}
                onChange={handleChange}
                placeholder="Ex: Tomar após refeições. Esta observação será preenchida automaticamente nas receitas."
                rows={3}
                data-testid="produto-observacao-padrao-input"
              />
              <p className="text-xs text-slate-500">
                Esta observação será preenchida automaticamente ao adicionar este produto a uma receita, mas pode ser alterada.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4 pt-4">
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                disabled={loading}
                data-testid="produto-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    A guardar...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {isEdit ? 'Atualizar' : 'Criar'} Produto
                  </>
                )}
              </Button>
              <Link to="/produtos">
                <Button type="button" variant="outline">
                  Cancelar
                </Button>
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
